import { normalizeClassId, getClassLevel } from '../classIdentity.mjs';
import openHand from './monk/open-hand.mjs';
import champion from './fighter/champion.mjs';
import thief from './rogue/thief.mjs';
import berserker from './barbarian/berserker.mjs';
import hunter from './ranger/hunter.mjs';
import devotion from './paladin/devotion.mjs';
import life from './cleric/life.mjs';
import evoker from './wizard/evoker.mjs';
import lore from './bard/lore.mjs';
import land from './druid/land.mjs';

export const FEATURE_ID_PATTERN = /^[a-z0-9][a-z0-9-]*(?::[a-z0-9][a-z0-9-]*){1,2}$/;
export const SUPPORTED_CLASS_IDS = Object.freeze(['barbarian', 'rogue', 'fighter', 'monk', 'ranger', 'paladin', 'cleric', 'wizard', 'bard', 'druid']);

export function validateSubclassDefinitions(definitions, validClassIds = SUPPORTED_CLASS_IDS) {
  const definitionIds = new Set();
  const featureIds = new Set();
  const subclassKeys = new Set();
  for (const definition of definitions) {
    const classId = normalizeClassId(definition?.classId);
    const id = String(definition?.id || '');
    if (!validClassIds.includes(classId)) throw new Error(`Invalid parent class: ${classId || '(missing)'}`);
    if (!id || definition.definitionId !== `${classId}:${id}`) throw new Error(`Invalid subclass namespace: ${definition?.definitionId}`);
    if (definitionIds.has(definition.definitionId)) throw new Error(`Duplicate definitionId: ${definition.definitionId}`);
    if (subclassKeys.has(`${classId}:${id}`)) throw new Error(`Duplicate subclass id within class: ${id}`);
    if (!Number.isInteger(definition.selectionLevel) || definition.selectionLevel < 1) throw new Error(`Invalid selectionLevel: ${definition.definitionId}`);
    definitionIds.add(definition.definitionId); subclassKeys.add(`${classId}:${id}`);
    for (const feature of definition.features || []) {
      if (!FEATURE_ID_PATTERN.test(feature.id) || !feature.id.startsWith(`${definition.definitionId}:`)) throw new Error(`Invalid feature namespace: ${feature.id}`);
      if (featureIds.has(feature.id)) throw new Error(`Duplicate feature id: ${feature.id}`);
      if (!Number.isInteger(feature.level) || feature.level < definition.selectionLevel) throw new Error(`Invalid feature level: ${feature.id}`);
      if (feature.choice !== undefined && (!feature.choice?.key || !feature.choice?.value)) throw new Error(`Invalid feature choice: ${feature.id}`);
      featureIds.add(feature.id);
    }
  }
  return true;
}

export function createSubclassRegistry(definitions) {
  validateSubclassDefinitions(definitions);
  const ordered = Object.freeze([...definitions]);
  const byDefinitionId = new Map(ordered.map((definition) => [definition.definitionId, definition]));
  const aliases = new Map();
  ordered.forEach((definition) => (definition.aliases || []).forEach((alias) => aliases.set(`${definition.classId}:${alias}`, definition.id)));
  const normalizeSubclassId = (classId, subclassId) => {
    const parent = normalizeClassId(classId); const value = String(subclassId ?? '').trim().toLowerCase();
    return aliases.get(`${parent}:${value}`) || value || null;
  };
  return Object.freeze({
    definitions: ordered,
    normalizeSubclassId,
    getSubclass: (classId, subclassId) => byDefinitionId.get(`${normalizeClassId(classId)}:${normalizeSubclassId(classId, subclassId)}`) || null,
    getSubclassesForClass: (classId) => ordered.filter((definition) => definition.classId === normalizeClassId(classId)),
  });
}

export const subclassRegistry = createSubclassRegistry([berserker, thief, champion, openHand, hunter, devotion, life, evoker, lore, land]);
export const { getSubclass, getSubclassesForClass, normalizeSubclassId } = subclassRegistry;

export function getSelectedSubclassId(character, classId) {
  const parent = normalizeClassId(classId);
  const state = character?.classState?.[parent];
  const legacyOccupation = (character?.occupation || []).find((entry) => normalizeClassId(entry?.class ?? entry?.Occupation ?? entry?.Name ?? entry?.occupation ?? entry?.name) === parent);
  const raw = state?.subclassId ?? state?.subclass?.id ?? (typeof state?.subclass === 'string' ? state.subclass : null)
    ?? legacyOccupation?.Subclass ?? legacyOccupation?.subclass ?? legacyOccupation?.subClass;
  return raw ? normalizeSubclassId(parent, raw) : null;
}

export const getSubclassLevel = (character, classId, subclassId) => {
  const parent = normalizeClassId(classId);
  const selected = getSelectedSubclassId(character, parent);
  const definition = getSubclass(parent, subclassId);
  return definition && selected === definition.id ? getClassLevel(character, parent) : 0;
};

export function getSubclassFeatureEntitlements(character) {
  return subclassRegistry.definitions.flatMap((definition) => {
    const level = getSubclassLevel(character, definition.classId, definition.id);
    if (level < definition.selectionLevel) return [];
    // A feature with `choice` (e.g. Hunter's Prey options) is entitled only while that option is chosen.
    const chosen = (feature) => !feature.choice || character?.classState?.[definition.classId]?.[feature.choice.key]?.id === feature.choice.value;
    return definition.features.filter((feature) => feature.level <= level && chosen(feature)).map((feature) => ({ ...feature, source: { type: 'subclass', classId: definition.classId, subclassId: definition.id } }));
  });
}

export const hasSubclassFeature = (character, featureId) => getSubclassFeatureEntitlements(character).some((feature) => feature.id === featureId);

export function derivePendingSubclassChoices(character) {
  const classIds = [...new Set(subclassRegistry.definitions.map(({ classId }) => classId))];
  return classIds.filter((classId) => getClassLevel(character, classId) >= Math.min(...getSubclassesForClass(classId).map(({ selectionLevel }) => selectionLevel)) && !getSelectedSubclassId(character, classId))
    .map((classId) => ({ classId, choiceType: 'subclass', selectionLevel: Math.min(...getSubclassesForClass(classId).map(({ selectionLevel }) => selectionLevel)), status: 'available' }));
}

validateSubclassDefinitions(subclassRegistry.definitions);

